import React, { useEffect, useMemo, useState } from "react";

const API =
  process.env.NODE_ENV === "production"
    ? "https://ecovanta.onrender.com"
    : "http://localhost:5000";

function startOfDay(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

function formatDate(date) {
  if (!date) return "-";
  return new Date(date).toLocaleDateString();
}

export default function FollowUpDashboard() {
  const [leads, setLeads] = useState([]);
  const [status, setStatus] = useState("Loading follow-ups...");

  useEffect(() => {
    async function loadLeads() {
      try {
        const response = await fetch(`${API}/request-access`, {
          credentials: "include"
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data?.error || "Unable to load follow-ups.");
        }

        setLeads(Array.isArray(data) ? data : []);
        setStatus("");
      } catch (error) {
        console.error("Load follow-ups failed:", error);
        setStatus(`Failed to load follow-ups: ${error.message}`);
      }
    }

    loadLeads();
  }, []);

  const grouped = useMemo(() => {
    const today = startOfDay(new Date());

    const result = {
      overdue: [],
      today: [],
      upcoming: [],
      none: []
    };

    leads.forEach((lead) => {
      if (!lead.nextFollowUp) {
        result.none.push(lead);
        return;
      }

      const followUpDate = startOfDay(lead.nextFollowUp);

      if (followUpDate < today) result.overdue.push(lead);
      else if (followUpDate.getTime() === today.getTime()) result.today.push(lead);
      else result.upcoming.push(lead);
    });

    result.overdue.sort((a, b) => new Date(a.nextFollowUp) - new Date(b.nextFollowUp));
    result.today.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
    result.upcoming.sort((a, b) => new Date(a.nextFollowUp) - new Date(b.nextFollowUp));

    return result;
  }, [leads]);

  return (
    <main style={{ maxWidth: 1200, margin: "0 auto", padding: 40 }}>
      <h1>Follow-Up Dashboard</h1>

      <p>
        Track overdue, due today, and upcoming sales follow-ups from Ecovanta
        Request Access leads.
      </p>

      <p>
        <a href="/admin/leads">Back to Lead Management</a>
      </p>

      {status && <p>{status}</p>}

      {!status && (
        <>
          <section style={sectionStyle}>
            <h2 style={{ color: "#dc2626" }}>
            🔴 Overdue ({grouped.overdue.length})
            </h2>
            <LeadList leads={grouped.overdue} emptyText="No overdue follow-ups." />
          </section>

          <section style={sectionStyle}>
           <h2 style={{ color: "#d97706" }}>
            🟡 Due Today ({grouped.today.length})
            </h2>
            <LeadList leads={grouped.today} emptyText="No follow-ups due today." />
          </section>

          <section style={sectionStyle}>
            <h2 style={{ color: "#16a34a" }}>
            🟢 Upcoming ({grouped.upcoming.length})
            </h2>
            <LeadList leads={grouped.upcoming} emptyText="No upcoming follow-ups." />
          </section>

          <section style={sectionStyle}>
            <h2 style={{ color: "#64748b" }}>
            ⚪ No Follow-Up Date ({grouped.none.length})
            </h2>
            <LeadList leads={grouped.none} emptyText="All leads have follow-up dates." />
          </section>
        </>
      )}
    </main>
  );
}

function LeadList({ leads, emptyText }) {
  if (!leads.length) {
    return <p>{emptyText}</p>;
  }

  return (
    <div style={{ display: "grid", gap: 12 }}>
      {leads.map((lead) => (
        <div key={lead._id} style={cardStyle}>
          <h3 style={{ marginTop: 0 }}>{lead.company || lead.name}</h3>

          <p>
            <strong>Name:</strong> {lead.name}
          </p>

          <p>
            <strong>Email:</strong>{" "}
            <a href={`mailto:${lead.email}`}>{lead.email}</a>
          </p>

          <p>
            <strong>Pipeline:</strong> {lead.stage || "New"}
          </p>

          <p>
            <strong>Owner:</strong> {lead.owner || "-"}
          </p>

          <p>
            <strong>Next Follow-Up:</strong> {formatDate(lead.nextFollowUp)}
          </p>

          {lead.notes && (
            <p>
              <strong>Notes:</strong> {lead.notes}
            </p>
          )}
        </div>
      ))}
    </div>
  );
}

const sectionStyle = {
  marginTop: 28,
  padding: 20,
  border: "1px solid #e5e7eb",
  borderRadius: 12,
  background: "#f8fafc"
};

const cardStyle = {
  padding: 16,
  border: "1px solid #e5e7eb",
  borderRadius: 10,
  background: "#ffffff"
};